import { useState, useEffect } from "react";
import {
  Avatar,
  ListItem,
  ListItemButton,
  ListItemText,
  Stack,
  Modal,
  Box,
  Skeleton,
  Button
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { BIBlue, BIGrey } from "../../assets/helpers";
import defaultPicture from "../../assets/images/profile_picture_placeholder.png";
import { useDispatch } from "react-redux";
import { getChatgroups, navigateToChatPage } from "../../common/Helper";
import { English } from "../../constant/EnglishFallback";
import NewChatModal from "../chat/NewChatModal";
import { getChatsFromIndexedDB, saveChatsToIndexedDB } from "../../db";

const SendInChatModal = ({ isOpen, onClose, title, imageUrl, fromTo }) => {
  const getUrl = window.location.pathname.split("/").pop();

  const { loginData, multiLanguageData } = useSelector((state) => state?.main);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openNewChatModal, setOpenNewChatModal] = useState(false);

  const {
    BUTTON_CANCEL = English?.SendInChatModal?.BUTTON_CANCEL,
    TEXT_NEW_CHAT = English?.SendInChatModal?.TEXT_NEW_CHAT,
    TEXT_AND = English?.SendInChatModal?.TEXT_AND,
    TEXT_OTHER = English?.SendInChatModal?.TEXT_OTHER,
    TEXT_NO_DATA = English?.SendInChatModal?.TEXT_NO_DATA
  } = multiLanguageData?.SendInChatModal || {};

  useEffect(() => {
    setLoading(true);
    getMembersList();
  }, []);

  const getMembersList = async () => {
    try {
      const cachedChats = await getChatsFromIndexedDB(); // 1. Try to load from IndexedDB first (instant load)
      if (cachedChats && cachedChats?.length > 0) {
        setMembers(cachedChats);
      } else {
        let getGroupList = await getChatgroups(dispatch, loginData);
        setMembers(getGroupList);
        if (getGroupList && getGroupList.length > 0) {
          // Store in IndexedDB for next time
          await saveChatsToIndexedDB(getGroupList);
        }
      }
    } catch (err) {
      console.log("getMembersList-> Error When Getting Chat Group Dashboard: ", err);
    } finally {
      setLoading(false);
    }
  };

  const navigateToChat = (item) => {
    navigateToChatPage(navigate, dispatch, fromTo, title, imageUrl, item?.chatGroupId, getUrl);
  };

  const UserListSkeleton = () => {
    return (
      <Stack
        spacing={0}
        sx={{ height: "450px", backgroundColor: "#fff", borderRadius: "8px", overflow: "hidden" }}>
        {[...Array(6)].map((_, index) => (
          <Box
            key={index}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              py: 1.5,
              px: 2,
              minHeight: "72px",
              borderBottom: index < 5 ? "1px solid #f0f0f0" : "none"
            }}>
            {/* Avatar Skeleton */}
            <Skeleton variant="circular" width={48} height={48} animation="wave" />

            {/* Content Skeleton */}
            <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 0.5 }}>
              {/* Name and timestamp row */}
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  width: "100%"
                }}>
                <Skeleton
                  variant="text"
                  width={`${Math.random() * 40 + 30}%`}
                  height={20}
                  animation="wave"
                />
              </Box>
              <Skeleton variant="rounded" width={40} height={14} animation="wave" />
            </Box>
          </Box>
        ))}
      </Stack>
    );
  };

  return (
    <>
      <Modal open={isOpen} onClose={onClose}>
        <div className="user-modal-responsive">
          <div>
            <div>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  color: BIBlue,
                  padding: "2px",
                  marginBottom: "5px"
                }}>
                <Button style={{ cursor: "pointer" }} onClick={onClose}>
                  {BUTTON_CANCEL}
                </Button>
                {!loading && (
                  <Button
                    style={{ cursor: "pointer" }}
                    onClick={() => {
                      setOpenNewChatModal(true);
                    }}>
                    {TEXT_NEW_CHAT}
                  </Button>
                )}
              </div>
              <Stack
                spacing={0}
                className="user-list"
                sx={{
                  height: "450px",
                  backgroundColor: "#fff",
                  borderRadius: "8px",
                  overflow: "scroll",
                  scrollbarWidth: "none",
                  padding: "0px"
                }}>
                {loading ? (
                  <UserListSkeleton />
                ) : (
                  <>
                    {members?.length !== 0 ? (
                      <>
                        {members
                          .sort((a, b) => new Date(b?.dateSent) - new Date(a?.dateSent))
                          .map((item, index) => {
                            return (
                              <div
                                key={index.toString()}
                                style={{
                                  border: "1px solid lightgrey",
                                  borderRadius: "5px",
                                  gap: "10px",
                                  marginBottom: "10px"
                                }}>
                                <ListItem
                                  key={index.toString()}
                                  disablePadding
                                  className="list-item"
                                  onClick={() => navigateToChat(item)}>
                                  <ListItemButton
                                    sx={{
                                      display: "flex",
                                      flexDirection: "row",
                                      gap: "10px"
                                    }}>
                                    <div
                                      style={{
                                        display: "flex",
                                        flexDirection: "row"
                                      }}>
                                      <Stack direction="row" spacing={1} alignItems="center">
                                        {item?.nrOfUsersInGroup > 2 ? (
                                          <>
                                            <Avatar
                                              src={item?.groupUserPictureUrl1 ?? defaultPicture}
                                              style={{ zIndex: 1 }}
                                              sx={{ width: 30, height: 30 }}
                                            />
                                            <Avatar
                                              src={item?.groupUserPictureUrl2 ?? defaultPicture}
                                              style={{
                                                zIndex: 0,
                                                marginTop: "-10px",
                                                marginLeft: "-10px"
                                              }}
                                              sx={{ width: 30, height: 30 }}
                                            />
                                          </>
                                        ) : (
                                          <Avatar src={item?.pictureUrl ?? defaultPicture} />
                                        )}
                                      </Stack>
                                    </div>
                                    <ListItemText
                                      primary={
                                        <div style={{ display: "flex" }}>
                                          <div>{item?.name?.trim()}</div>
                                        </div>
                                      }
                                    />
                                  </ListItemButton>
                                </ListItem>
                              </div>
                            );
                          })}
                      </>
                    ) : (
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          width: "100%",
                          height: 200,
                          color: BIGrey
                        }}>
                        <h3>{TEXT_NO_DATA}</h3>
                      </div>
                    )}
                  </>
                )}
              </Stack>
            </div>
          </div>
        </div>
      </Modal>
      {openNewChatModal && (
        <NewChatModal
          open={openNewChatModal}
          onClose={() => setOpenNewChatModal(false)}
          title={title}
          imageUrl={imageUrl}
          from={fromTo}
          getUrl={getUrl}
        />
      )}
    </>
  );
};

export default SendInChatModal;
